import axios, { AxiosRequestConfig, AxiosResponse, AxiosError } from 'axios'
import qs from 'qs'
import type {
  HomeImageListItem,
  HomeImageCreateRequest,
  MessageCounts,
  LoginRequest,
  LoginResponse,
  SetLocaleRequest,
  NotificationItem,
  NotificationResponse,
  NotificationCounts,
  CurrentSession,
  FeaturedItemResource,
  ServerResource,
  ServiceResource,
  MapServerResponse,
  FeaturesData,
} from './models'

declare global {
  interface Window {
    csrtCookieName?: string
    g_csrfCookieName?: string
  }
}

export default class Client {
  config: AxiosRequestConfig

  constructor(baseURL = '/api/') {
    this.config = {
      baseURL,
      xsrfCookieName: window.csrtCookieName || window.g_csrfCookieName || 'csrftoken',
      xsrfHeaderName: 'X-CSRFToken',
    }
  }

  appendParams = (url: string, params?: { [key: string]: any }): string => {
    if (params) {
      return `${url}?${qs.stringify(params)}`
    }
    return url
  }

  _get = (path: string, params?: { [key: string]: any }, config: AxiosRequestConfig = {}): Promise<AxiosResponse> => {
    return axios.get(this.appendParams(path, params), { ...this.config, ...config })
  }

  _post = (
    path: string,
    data?: { [key: string]: any } | string,
    params?: { [key: string]: any },
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse> => {
    return axios.post(this.appendParams(path, params), data, { ...this.config, ...config })
  }

  _patch = (
    path: string,
    data?: { [key: string]: any } | string,
    params?: { [key: string]: any },
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse> => {
    return axios.patch(this.appendParams(path, params), data, { ...this.config, ...config })
  }

  _put = (
    path: string,
    data?: { [key: string]: any } | string,
    params?: { [key: string]: any },
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse> => {
    return axios.put(this.appendParams(path, params), data, { ...this.config, ...config })
  }

  _delete = (
    path: string,
    params?: { [key: string]: any },
    config: AxiosRequestConfig = {},
  ): Promise<AxiosResponse> => {
    return axios.delete(this.appendParams(path, params), { ...this.config, ...config })
  }

  login = (data: LoginRequest): Promise<boolean | LoginResponse> => {
    return this._post('/auth/api/login/', data, {}, { baseURL: '' })
      .then(response => response.data as LoginResponse)
      .catch((error: AxiosError) => {
        if (error.response?.status == 401) {
          return false
        }
        throw error
      })
  }

  getCurrentSession = (): Promise<CurrentSession> => {
    return this._get('/v2/auth/current-session/').then(response => response.data as CurrentSession)
  }

  getNotificationCounts = (): Promise<NotificationCounts> => {
    return this._get('/v1/notifications/counts/').then(response => response.data as NotificationCounts)
  }

  markNotificationsAsRead = (): Promise<boolean> => {
    return this._put('/v1/notifications/mark-read/').then(() => true)
  }

  getNotifications = (): Promise<NotificationItem[]> => {
    return this._get('/v1/notifications/').then(response => (response.data as NotificationResponse).objects)
  }

  setLocale = (data: SetLocaleRequest): Promise<string | null> => {
    let formData = `language=${encodeURIComponent(data.language)}`
    if (data.next) {
      formData += `&next=${encodeURIComponent(data.next)}`
    }

    return this._post('/locale/set/', formData, {}, { baseURL: '' }).then(
      response => response.headers['location'] || null,
    )
  }

  getHomeImageList = (): Promise<HomeImageListItem[]> => {
    return this._get('/v2/home-images/').then(response => response.data as HomeImageListItem[])
  }

  createHomeImageItem = (data: HomeImageCreateRequest): Promise<HomeImageListItem> => {
    const formData = new FormData()
    formData.append('image', data.image)
    formData.append('category', data.category)
    formData.append('caption', data.caption)

    return this._post('/v2/home-images/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then(response => response.data as HomeImageListItem)
  }

  deleteHomeImageItem = (id: number): Promise<boolean> => {
    const path = `/v2/home-images/${id}/`

    return new Promise((resolve, reject) => {
      this._delete(path)
        .then(() => true)
        .catch((error: AxiosError) => {
          if (error.response?.status == 410) {
            resolve(true)
            return true
          } else {
            reject(error)
          }
        })
    })
  }

  getMessageCounts = (): Promise<MessageCounts> => {
    return this._get('/v1/message-threads/counts/').then(response => response.data as MessageCounts)
  }

  getPageContext = (page: string): Promise<any> => {
    return this._get(`/v2/page-context/${page}/`).then(response => response.data)
  }

  getFeaturedItems = (limit?: number, offset?: number): Promise<FeaturedItemResource[]> => {
    return this._get('/v1/featured-items/', { limit, offset, order_by: '-date_featured' }).then(
      response => response.data.objects as FeaturedItemResource[],
    )
  }

  getServers = async (): Promise<ServerResource[]> => {
    const servers: ServerResource[] = []
    let uri: string | null = 'v1/mapservers/servers/'

    while (uri) {
      const response: MapServerResponse<ServerResource> = await this._get(uri).then()
      servers.push(...response.data.objects)
      const next = response.data.meta.next
      if (next) {
        uri = next.replace('/api', '')
      } else {
        uri = null
      }
    }

    return servers
  }

  updateServer = (uri: string, data: { [key: string]: any } | string): Promise<void> => {
    const formattedURI = uri.startsWith('/api') ? uri.replace('/api', '') : uri
    return this._patch(formattedURI, data).then()
  }

  removeServer = (uri: string): Promise<void> => {
    const formattedURI = uri.startsWith('/api') ? uri.replace('/api', '') : uri
    return this._delete(formattedURI).then()
  }

  getServices = async (server: number): Promise<ServiceResource[]> => {
    const services: ServiceResource[] = []
    let uri: string | null = `v1/mapservers/services/?server=${server}`

    while (uri) {
      const response: MapServerResponse<ServiceResource> = await this._get(uri).then()
      services.push(...response.data.objects)
      const next = response.data.meta.next
      if (next) {
        uri = next.replace('/api', '')
      } else {
        uri = null
      }
    }

    return services
  }

  removeServices = (server: number, services: string[]): Promise<any> => {
    const data = {
      objects: [],
      deleted_objects: services,
    }
    return this._patch('v1/mapservers/services/', data)
  }

  getFeatures = (): Promise<FeaturesData> => {
    return this._get('/v2/subscriptions/subscribed-features/').then(response => response.data as FeaturesData)
  }
}
